
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useToast } from './ToastProvider';
import FormField from './common/FormField';
import { useForm } from './common/useForm';
import './AdminListingManager.css';

const AdminListingManager = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, handleChange, setForm] = useForm({
    title: '',
    section: 'info',
    category: '',
    description: '',
    imageUrl: ''
  });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/content', { params: { limit: 1000 } });
      setListings(response.data.items || []);
    } catch (err) {
      const msg = err.response?.data?.message || 'Зар авах үед алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      let submitForm = { ...form };

      if (imageFile) {
        const uploadData = new FormData();
        uploadData.append('file', imageFile);
        const uploadResponse = await api.post('/upload', uploadData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        submitForm.imageUrl = uploadResponse.data.file.url;
      }

      if (editingId) {
        await api.put(`/content/${editingId}`, submitForm);
        showToast('Зар амжилттай засагдлаа!', 'success');
      } else {
        await api.post('/content', submitForm);
        showToast('Шинэ зар амжилттай нэмэгдлээ!', 'success');
      }

      setForm({ title: '', section: 'info', category: '', description: '', imageUrl: '' });
      setImageFile(null);
      setShowForm(false);
      setEditingId(null);
      fetchListings();
    } catch (err) {
      const msg = err.response?.data?.message || 'Алдаа гарлаа';
      setError(msg);
      showToast(msg, 'error');
    }
  };

  const handleEdit = (listing) => {
    setForm({
      title: listing.title,
      section: listing.section || 'info',
      category: listing.category,
      description: listing.description,
      imageUrl: listing.imageUrl || ''
    });
    setImageFile(null);
    setEditingId(listing._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Энэ зарыг устгахдаа итгэлтэй байна уу?')) {
      try {
        await api.delete(`/content/${id}`);
        showToast('Зар амжилттай устгагдлаа!', 'success');
        fetchListings();
      } catch (err) {
        const msg = err.response?.data?.message || 'Устгахад алдаа гарлаа';
        setError(msg);
        showToast(msg, 'error');
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ title: '', section: 'info', category: '', description: '', imageUrl: '' });
    setImageFile(null);
  };

  return (
    <div className="admin-listing-manager">
      <div className="listing-manager-header">
        <h2>Зарын удирдлага</h2>
        <button
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Цуцлах' : '+ Шинэ зар нэмэх'}
        </button>
      </div>

      {/* Toast мэдэгдэл рүү шилжүүлсэн */}

      {showForm && (
        <div className="listing-form-container">
          <form onSubmit={handleSubmit} className="listing-form">
            <h3>{editingId ? 'Зар засах' : 'Шинэ зар нэмэх'}</h3>
            <FormField
              type="input"
              label="Гол төрөл"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              placeholder="жишээ: Машины засвар"
            />
            <FormField
              type="select"
              label="Ангилал"
              name="category"
              value={form.category}
              onChange={handleChange}
              required
              options={[
                { value: '', label: 'Сонгоно уу' },
                { value: 'service', label: 'Үйлчилгээ' },
                { value: 'product', label: 'Бүтээгдэхүүн' }
              ]}
            />
            <FormField
              type="textarea"
              label="Тайлбар"
              name="description"
              value={form.description}
              onChange={handleChange}
              required
              placeholder="Нарийвчилсан тайлбар"
              rows={4}
            />
            <div className="form-group">
              <label>Зураг хийх</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  setImageFile(file || null);
                }}
              />
              {imageFile && <p style={{ marginTop: 8, color: '#374151' }}>Сонгогдсон файл: {imageFile.name}</p>}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Хадгалах' : 'Нэмэх'}
              </button>
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Цуцлах
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="listings-list">
        {loading ? (
          <p>Уншиж байна...</p>
        ) : listings.length === 0 ? (
          <p>Одоогоор зар байхгүй байна</p>
        ) : (
          <div className="listings-grid">
            {listings.map((listing) => (
              <div key={listing._id} className="listing-card">
                <div className="listing-card-header">
                  <h3>{listing.title}</h3>
                  <span className="category-badge">{listing.category}</span>
                </div>
                
                {listing.imageUrl && (
                  <img src={listing.imageUrl} alt={listing.title} className="listing-image" />
                )}

                <p className="listing-description">{listing.description}</p>

                <div className="listing-stats">
                  <span>Үзсэн: {listing.viewCount || 0}</span>
                  <span>Нийтлэгдсэн: {new Date(listing.createdAt).toLocaleDateString('mn-MN')}</span>
                </div>

                <div className="listing-actions">
                  <button
                    className="btn-edit"
                    onClick={() => handleEdit(listing)}
                  >
                    Засах
                  </button>
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(listing._id)}
                  >
                    Устгах
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminListingManager;
